import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, takeUntil } from 'rxjs/operators';
import { ApiService } from '../../services/api.service';
import * as CoreUiActions from './core-ui.actions';
import * as fromCoreUi from './core-ui.reducer';
import { DataPersistence, MessageService } from '../../services';

@Injectable()
export class CoreUiEffects {
  loadCoreUi$ = createEffect(() =>
    this.dataPersistence.fetch(CoreUiActions.loadCoreUi, {
      run: (action: ReturnType<typeof CoreUiActions.loadCoreUi>, state: fromCoreUi.CoreUiPartialState) => {
        // Your custom service 'load' logic goes here. For now just return a success action...
        return CoreUiActions.loadCoreUiSuccess({ coreUi: [] });
      },

      onError: (action: ReturnType<typeof CoreUiActions.loadCoreUi>, error) => {
        console.error('Error', error);
        return CoreUiActions.loadCoreUiFailure({ error });
      },
    })
  );

  // navigateToUrl$: Observable<void> = ofType(navigateToUrl).
  navigateToUrl$ = createEffect(() =>
    this.dataPersistence.fetch(CoreUiActions.navigateToUrl, {
      run: (action: ReturnType<typeof CoreUiActions.navigateToUrl>, state: fromCoreUi.CoreUiPartialState) => {
        // this.router.navigate([action.url]);
        if (action.url === 'history-back') {
          window.history.back();
        } else {
          this.router.navigateByUrl(action.url);
        }
      },
    })
  );

  creatQuickSale$ = createEffect(() =>
    this.dataPersistence.fetch(CoreUiActions.createQuickSale, {
      run: (action: ReturnType<typeof CoreUiActions.createQuickSale>, state: fromCoreUi.CoreUiPartialState) => {
        this.messageService.createQuickSale();
      },
    })
  );

  loadCustomers$ = createEffect(() =>
    this.dataPersistence.fetch(CoreUiActions.searchCustomers, {
      run: (action: ReturnType<typeof CoreUiActions.searchCustomers>, state: fromCoreUi.CoreUiPartialState) => {
        return this.apiService
          .customerSearch({
            customerNumber: 0,
            customerfuzzySearch: action.customerfuzzySearch,
            customerType: action.customerType,
          })
          .pipe(
            takeUntil(this.actions$.pipe(ofType(CoreUiActions.clearSearchData))),
            map((result) => {
              return CoreUiActions.searchCustomersSuccess({ result });
            })
          );
      },
      onError: (action: ReturnType<typeof CoreUiActions.searchCustomers>, error) => {
        return CoreUiActions.searchCustomersFailed(error);
      },
    })
  );

  loadExternalCustomers$ = createEffect(() =>
    this.dataPersistence.fetch(CoreUiActions.searchExternalCustomers, {
      run: (action: ReturnType<typeof CoreUiActions.searchExternalCustomers>, state: fromCoreUi.CoreUiPartialState) => {
        return this.apiService
          .customerExternalSearch({
            searchString: action.searchString,
          })
          .pipe(
            takeUntil(this.actions$.pipe(ofType(CoreUiActions.clearExternalSearchData))),
            map((result) => {
              return CoreUiActions.searchExternalCustomersSuccess({ result });
            })
          );
      },
      onError: (action: ReturnType<typeof CoreUiActions.searchExternalCustomers>, error) => {
        return CoreUiActions.searchExternalCustomersFailed(error);
      },
    })
  );

  getBrandDefaults$ = createEffect(() =>
    this.dataPersistence.fetch(CoreUiActions.loadBrandDefaults, {
      run: (action: ReturnType<typeof CoreUiActions.loadBrandDefaults>, state: fromCoreUi.CoreUiPartialState) => {
        return this.apiService.fetchBrandDefaults().pipe(
          map((res) => {
            return CoreUiActions.loadBrandDefaultsSuccess({
              result: res.SearchResults[0],
            });
          })
        );
      },
      onError: (action: ReturnType<typeof CoreUiActions.loadBrandDefaults>, error) => {
        return CoreUiActions.loadBrandDefaultsFailed(error);
      },
    })
  );

  loadCommunicationGroupList$ = createEffect(() =>
    this.dataPersistence.fetch(CoreUiActions.loadCommunicationGroupList, {
      run: (action: ReturnType<typeof CoreUiActions.loadCommunicationGroupList>, state: fromCoreUi.CoreUiPartialState) => {
        if (state.coreUi.cachedCommunicationGroupList) {
          return CoreUiActions.loadCommunicationGroupListSuccess({
            communicationGroupList: state.coreUi.cachedCommunicationGroupList,
          });
        }
        return this.apiService.loadCommunicationGroupList().pipe(
          map((res) => {
            return CoreUiActions.loadCommunicationGroupListSuccess({
              communicationGroupList: res?.SearchResults,
            });
          })
        );
      },
      onError: (action: ReturnType<typeof CoreUiActions.loadCommunicationGroupList>, error) => {
        return CoreUiActions.loadCommunicationGroupListFailed(error);
      },
    })
  );

  loadArbCentralNavSecurity$ = createEffect(() =>
    this.dataPersistence.fetch(CoreUiActions.loadArbCentralNavSecurity, {
      run: (action: ReturnType<typeof CoreUiActions.loadArbCentralNavSecurity>, state: fromCoreUi.CoreUiPartialState) => {
        if (state.coreUi.arbCentralNavSecurity) {
          return CoreUiActions.loadArbCentralNavSecuritySuccess({
            arbCentralNavSecurity: state.coreUi.arbCentralNavSecurity,
          });
        } else {
          return this.apiService.loadARBCentralBarSetting().pipe(
            map((res) => {
              return CoreUiActions.loadArbCentralNavSecuritySuccess({
                arbCentralNavSecurity: res,
              });
            })
          );
        }
      },
      onError: (action: ReturnType<typeof CoreUiActions.loadArbCentralNavSecurity>, error) => {
        return CoreUiActions.loadArbCentralNavSecurityFailed(error);
      },
    })
  );

  fetchSiteList$ = createEffect(() =>
    this.dataPersistence.fetch(CoreUiActions.fetchSiteList, {
      run: (action: ReturnType<typeof CoreUiActions.fetchSiteList>, state: fromCoreUi.CoreUiPartialState) => {
        if (state.coreUi.cachedSiteList.siteList && state.coreUi.cachedSiteList?.siteList.length > 0) {
          return CoreUiActions.fetchSiteListSuccess({
            cachedSiteList: state.coreUi.cachedSiteList,
          });
        } else {
          return this.apiService.fetchSiteList().pipe(
            map((res) => {
              return CoreUiActions.fetchSiteListSuccess({
                cachedSiteList: res,
              });
            })
          );
        }
      },
      onError: (action: ReturnType<typeof CoreUiActions.fetchSiteList>, error) => {
        return CoreUiActions.fetchSiteListFailed(error);
      },
    })
  );

  fetchSalesPeopleList$ = createEffect(() =>
    this.dataPersistence.fetch(CoreUiActions.fetchSalesPeopleList, {
      run: (action: ReturnType<typeof CoreUiActions.fetchSalesPeopleList>, state: fromCoreUi.CoreUiPartialState) => {
        if (state.coreUi.cachedSalesPeopleList.length > 0) {
          return CoreUiActions.fetchSalesPeopleListSuccess({
            cachedSalesPeopleList: state.coreUi.cachedSalesPeopleList,
          });
        } else {
          return this.apiService.fetchSalesPeople().pipe(
            map((res) => {
              return CoreUiActions.fetchSalesPeopleListSuccess({
                cachedSalesPeopleList: res?.SearchResults,
              });
            })
          );
        }
      },
    })
  );

  loadShipToCustomers$ = createEffect(() =>
    this.dataPersistence.fetch(CoreUiActions.searchshipToCustomers, {
      run: (action: ReturnType<typeof CoreUiActions.searchshipToCustomers>, state: fromCoreUi.CoreUiPartialState) => {
        return this.apiService
          .customerShipToSearch(
            //customerfuzzySearch: action.searchString,

            { shipToNumber: action.customerNumber, customerfuzzySearch: action.customerfuzzySearch, customerType: action.customerType }
          )
          .pipe(
            takeUntil(this.actions$.pipe(ofType(CoreUiActions.clearshipToSearchData))),
            map((result) => {
              return CoreUiActions.searchshipToCustomersSuccess({ result });
            })
          );
      },
      onError: (action: ReturnType<typeof CoreUiActions.searchshipToCustomers>, error) => {
        return CoreUiActions.searchshipToCustomersFailed(error);
      },
    })
  );

   loadJobOrder$ = createEffect(() =>
    this.dataPersistence.fetch(CoreUiActions.searchJobOrder, {
      run: (action: ReturnType<typeof CoreUiActions.searchJobOrder>, state: fromCoreUi.CoreUiPartialState) => {
        return this.apiService
          .jobOrderSearch({
            jobSearchString: action.jobSearchString,
          })
          .pipe(
            takeUntil(this.actions$.pipe(ofType(CoreUiActions.clearSearchData))),
            map((result) => {
              return CoreUiActions.searchJobOrderSuccess({ result });
            })
          );
      },
      onError: (action: ReturnType<typeof CoreUiActions.searchJobOrder>, error) => {
        return CoreUiActions.searchJobOrderFailed(error);
      },
    })
  );

  constructor(
    private dataPersistence: DataPersistence<fromCoreUi.CoreUiPartialState>,
    private router: Router,
    private apiService: ApiService,
    private messageService: MessageService,
    private actions$: Actions
  ) {}
}

